<template>
  <div
    ref="raiz"
    class="opcoes-avancadas"
    data-assistente-ignorar
    @keydown.esc.stop.prevent="fechar"
  >
    <!--
      Discreto de propósito: é ferramenta de teste, ligada por variável de
      ambiente no agente. Quem não testa nem chega a ver (o dock só monta
      este componente com `opcoes.exibir`).
    -->
    <PontotelBotaoIcone
      data-testid="assistente-dock-opcoes-avancadas"
      tamanho="P"
      tipo="neutro"
      peso="intermediario"
      :aria-label="t('assistentePontotel.opcoesAvancadas.abrir')"
      :title="t('assistentePontotel.opcoesAvancadas.abrir')"
      :aria-expanded="aberto"
      :aria-controls="idDoPopover"
      @clique="alternar"
    >
      <template #icone>
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 7h10M18 7h2M4 17h4M12 17h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          <circle cx="16" cy="7" r="2" stroke="currentColor" stroke-width="2" />
          <circle cx="10" cy="17" r="2" stroke="currentColor" stroke-width="2" />
        </svg>
      </template>
    </PontotelBotaoIcone>
    <div
      v-if="aberto"
      :id="idDoPopover"
      ref="popover"
      class="opcoes-avancadas__popover"
      :style="estiloDoPopover"
      role="dialog"
      :aria-label="t('assistentePontotel.opcoesAvancadas.titulo')"
      data-testid="opcoes-avancadas-popover"
    >
      <p class="opcoes-avancadas__titulo">{{ t('assistentePontotel.opcoesAvancadas.titulo') }}</p>
      <div class="opcoes-avancadas__opcao">
        <PontotelToggle
          data-testid="opcoes-avancadas-tela"
          tamanho="M"
          :model-value="opcoes.acoesDeTela"
          :label="t('assistentePontotel.opcoesAvancadas.acoesDeTela')"
          @update:model-value="mudar('tela', $event)"
        />
        <span class="opcoes-avancadas__descricao">{{ t('assistentePontotel.opcoesAvancadas.acoesDeTelaDescricao') }}</span>
      </div>
      <div class="opcoes-avancadas__opcao">
        <PontotelToggle
          data-testid="opcoes-avancadas-sistema"
          tamanho="M"
          :model-value="opcoes.acoesDeSistema"
          :label="t('assistentePontotel.opcoesAvancadas.acoesDeSistema')"
          @update:model-value="mudar('sistema', $event)"
        />
        <span class="opcoes-avancadas__descricao">{{ t('assistentePontotel.opcoesAvancadas.acoesDeSistemaDescricao') }}</span>
      </div>
      <p class="opcoes-avancadas__aviso">{{ t('assistentePontotel.opcoesAvancadas.aviso') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { PontotelBotaoIcone, PontotelToggle } from '@Lar21/imaginii-web-components/lib/vue'
import type { OpcoesAvancadas } from '../../dominio/assistente.interface'
import { deslocamentoParaCaber, type ModoDeAcao } from './opcoesAvancadas'

/**
 * O botão de ajustes do cabeçalho do dock e o popover com os dois modos. Só
 * emite a escolha: quem guarda (store + localStorage) é a action
 * `definirModoDeAcao`. Fecha com Esc — sem deixar o Esc fechar o dock junto —
 * e com clique fora.
 */
defineProps<{ opcoes: OpcoesAvancadas }>()
const emit = defineEmits<{ 'update:modo-de-acao': [payload: { modo: ModoDeAcao, ligado: boolean }] }>()
const { t } = useI18n()

const raiz = ref<HTMLElement | null>(null)
const popover = ref<HTMLElement | null>(null)
const aberto = ref(false)
const estiloDoPopover = ref<Record<string, string>>({})
const idDoPopover = `opcoes-avancadas-${Math.random().toString(36).slice(2, 10)}`

function alternar (): void {
  aberto.value = !aberto.value
}

function fechar (): void {
  aberto.value = false
}

function mudar (modo: ModoDeAcao, ligado: boolean): void {
  emit('update:modo-de-acao', { modo, ligado: ligado === true })
}

/** Folga entre o popover e a borda de quem recorta, em px. */
const MARGEM_DO_POPOVER = 8

/** O primeiro ancestral que recorta o que vaza (o dock acoplado), ou a janela. */
function limiteDoPopover (elemento: HTMLElement): { left: number, right: number } {
  for (let atual = elemento.parentElement; atual; atual = atual.parentElement) {
    if (getComputedStyle(atual).overflowX !== 'visible') {
      const { left, right } = atual.getBoundingClientRect()
      return { left, right }
    }
  }
  return { left: 0, right: window.innerWidth }
}

async function caberNoLimite (): Promise<void> {
  estiloDoPopover.value = {}
  await nextTick()
  const elemento = popover.value
  if (!elemento || typeof window === 'undefined') return
  const { left, right } = elemento.getBoundingClientRect()
  const deslocamento = deslocamentoParaCaber({ left, right }, limiteDoPopover(elemento), MARGEM_DO_POPOVER)
  if (deslocamento !== 0) estiloDoPopover.value = { transform: `translateX(${Math.round(deslocamento)}px)` }
}

function aoClicarFora (evento: Event): void {
  const alvo = evento.target as Node | null
  if (raiz.value && alvo && !raiz.value.contains(alvo)) fechar()
}

watch(aberto, (estaAberto) => {
  if (typeof document === 'undefined') return
  if (estaAberto) void caberNoLimite()
  if (estaAberto) document.addEventListener('pointerdown', aoClicarFora, true)
  else document.removeEventListener('pointerdown', aoClicarFora, true)
})

onBeforeUnmount(() => {
  if (typeof document !== 'undefined') document.removeEventListener('pointerdown', aoClicarFora, true)
})
</script>

<style scoped>
.opcoes-avancadas {
  position: relative;
  display: inline-flex;
}

.opcoes-avancadas__popover {
  position: absolute;
  top: calc(100% + var(--espacamento-4));
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: var(--espacamento-10);
  box-sizing: border-box;
  width: min(280px, calc(100vw - 2 * var(--espacamento-16)));
  padding: var(--espacamento-12);
  border: var(--espessura-10) solid var(--cor-neutra-20);
  border-radius: var(--arredondamento-8);
  color: var(--cor-texto-principal);
  background: var(--cor-background-fundo-principal);
  box-shadow: 0 var(--espacamento-4) var(--espacamento-16) color-mix(in srgb, var(--cor-texto-principal) 14%, transparent);
  font-weight: normal;
}

.opcoes-avancadas__titulo {
  margin: 0;
  font-size: var(--texto-subtitulo-p-tamanho);
  font-weight: var(--texto-subtitulo-p-peso);
}

.opcoes-avancadas__opcao {
  display: flex;
  flex-direction: column;
  gap: var(--espacamento-2);
}

.opcoes-avancadas__descricao,
.opcoes-avancadas__aviso {
  color: var(--cor-neutra-100);
  font-size: var(--texto-legenda-pp-tamanho);
  line-height: var(--texto-legenda-pp-entrelinha);
}

.opcoes-avancadas__aviso {
  margin: 0;
  padding-top: var(--espacamento-8);
  border-top: var(--espessura-10) solid var(--cor-neutra-20);
}
</style>
